import React, { useMemo } from "react";
import { Box, Progress } from "@chakra-ui/react";
import { Circle, MapContainer, Marker, TileLayer } from "react-leaflet";
import { divIcon } from "leaflet";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import {
  LIGHTNING_COVERAGE_FILL_COLOR,
  LIGHTNING_COVERAGE_FILL_OPACITY,
  LIGHTNING_COVERAGE_STROKE_COLOR,
  LIGHTNING_COVERAGE_STROKE_WEIGHT,
} from "components/SensorAlertSection/lightning/lightning-constants";
import "./Styles.css";
import { DivIcon, Icon, IconOptions, LatLngExpression } from "leaflet";
import { EnvironmentalType } from "../../../enum/EnvironmentalType";
import { Center, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import { IInsightMultiStation } from "services/OceanWeatherIntelligenceService";
import { VisualizationType } from "enum/VisualizationType";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import { FlyTo } from "./util/FlyTo";
import { CenterOnStation } from "./util/CenterOnStation";
import { SearchControl } from "./util/SearchControl";
import RadarTileLayer from "./Components/RadarTileLayer";
import { ZoomLayerControl } from "./util/ZoomLayerControl";
import ForecastTileLayer from "./Components/ForecastTileLayer";
import { ILayerBounds } from "types/IForecastMap";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import DrawMapControls from "./DrawMapControls";
import PointSelectionMapControls from "./PointSelectionMapControls";
import {
  MapClickHandler,
  PointSelectionMapClickHandler,
} from "./util/MapClickHandler";
import { SensorSelectionMapClickHandler } from "./util/SensorSelectionMapClickHandler";
import CustomPointMarker from "./Components/CustomPointMarker";
import SensorSelectionMarker from "./Components/SensorSelectionMarker";
import SensorSelectionControls from "./SensorSelectionControls";
import { useRadarErrorNotification } from "./hooks/useRadarErrorNotification";
import { useRadarNetworkStatus } from "./hooks/useRadarNetworkStatus";
import RadarErrorBoundary from "./Components/RadarErrorBoundary";

export interface ForecastChartPanelMapProps {
  center?: LatLngExpression;
  zoom?: number;
  tabStateActivty: EnvironmentalType;
  loadingInsightMultiStation: boolean;
  clickMapLatLng: LatLngExpression;
  selectedStation: number;
  iconSelected: Icon<IconOptions> | DivIcon | undefined;
  customMarker: JSX.Element[];
  insightMode: boolean;
  insightMultiStation: IInsightMultiStation | null;
  visualizationType: VisualizationType;
  regionId: number;
  sensorMarkerData: ISensorMeasuredData[];
  mapCenterLatitude: number;
  mapCenterLongitude: number;
  searchedLatLng: LatLngExpression | null;
  radarTileUrl?: string;
  forecastTileLayerSource: {
    path: { [key: string]: string };
    bounds: ILayerBounds;
  } | null;
  showRadarTile?: boolean;
  showForecastTile?: boolean;
  clearSearchedLatLng: () => void;
  maxZoom?: number;
  selectedLayer: EnvironmentalVariables | null;
  mapTilerKey?: string;
  isAreaDrawingMode?: boolean;
  isPointSelectionMode?: boolean;
  isSensorSelectionMode?: boolean;
  selectedSensorIds?: number[];
}

const ForecastChartPanelMap: React.FC<ForecastChartPanelMapProps> = ({
  center,
  zoom,
  tabStateActivty,
  loadingInsightMultiStation,
  clickMapLatLng,
  selectedStation,
  iconSelected,
  customMarker,
  insightMode,
  insightMultiStation,
  visualizationType,
  regionId,
  sensorMarkerData,
  mapCenterLatitude,
  mapCenterLongitude,
  searchedLatLng,
  clearSearchedLatLng,
  radarTileUrl,
  forecastTileLayerSource,
  showRadarTile,
  showForecastTile,
  maxZoom,
  selectedLayer,
  mapTilerKey,
  isAreaDrawingMode,
  isPointSelectionMode,
  isSensorSelectionMode,
  selectedSensorIds,
}: ForecastChartPanelMapProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "forecastChart.forecastChartPanelMap",
  });

  // T025: Show error notifications for Rainbow AI radar errors
  useRadarErrorNotification();

  // T027: Monitor network status and handle offline/online transitions
  useRadarNetworkStatus();

  const selectedSensorIdSet = useMemo(
    () => new Set(selectedSensorIds),
    [selectedSensorIds],
  );

  const zIndex = useMemo(() => {
    if (selectedLayer?.includes("wave")) {
      return 650;
    }

    return 800;
  }, [selectedLayer]);

  const waterColor = useMemo(() => {
    if (selectedLayer || showRadarTile) {
      return "#c3c3c3";
    }

    return "#7bbacc";
  }, [selectedLayer, showRadarTile]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom={true}
      className="map"
      attributionControl={false}
      zoomControl={true}
      minZoom={2}
    >
      <style>
        {`
          .leaflet-container {
            background: ${waterColor}; 
          }
        `}
      </style>
      {!isAreaDrawingMode &&
        !isPointSelectionMode &&
        !isSensorSelectionMode && <MapClickHandler />}
      <FlyTo
        searchedLatLng={searchedLatLng}
        clearSearchedLatLng={clearSearchedLatLng}
      />
      <CenterOnStation
        latitude={mapCenterLatitude}
        longitude={mapCenterLongitude}
      />
      <SearchControl />
      <ZoomLayerControl
        showRadar={showRadarTile}
        showForecast={showForecastTile}
      />

      <TileLayer
        attribution={
          '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e'
        }
        tileSize={512}
        zoomOffset={-1}
        minZoom={1}
        crossOrigin={true}
        url={`https://api.maptiler.com/maps/019b6b50-0ba6-7f22-825f-03de06fca8a8/{z}/{x}/{y}.webp?key=${mapTilerKey}`}
        zIndex={700}
      />

      {!isSensorSelectionMode && showRadarTile && radarTileUrl && (
        <RadarErrorBoundary>
          <RadarTileLayer url={radarTileUrl} showRadar={showRadarTile} />
        </RadarErrorBoundary>
      )}
      {!isSensorSelectionMode && (
        <ForecastTileLayer
          layerSource={forecastTileLayerSource}
          visibility={!!showForecastTile}
          maxZoom={maxZoom}
          currentZoom={zoom}
          zIndex={zIndex}
        />
      )}
      {!isSensorSelectionMode && (
        <Center
          hidden={!loadingInsightMultiStation}
          bg="#0000005e"
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          top="30%"
          borderRadius="base"
          zIndex={99999}
          py="12px"
          px="16px"
        >
          <Box display="flex" flexDir="column" alignItems="center">
            <Progress
              colorScheme="blue"
              hasStripe={false}
              width="200px"
              size="xs"
              isIndeterminate
            />
            <Text color="white" fontWeight="medium">
              {t("loading")}...
            </Text>
          </Box>
        </Center>
      )}
      {!isSensorSelectionMode && clickMapLatLng && selectedStation === 0 && (
        <Marker position={clickMapLatLng} icon={iconSelected}></Marker>
      )}
      {!isSensorSelectionMode && (
        <div
          key={`${sensorMarkerData}-${insightMode} - ${insightMultiStation?.id} - ${tabStateActivty} - ${visualizationType} - ${loadingInsightMultiStation} - ${selectedStation}- ${regionId}`}
        >
          {customMarker}
        </div>
      )}
      {!isSensorSelectionMode && isAreaDrawingMode && <DrawMapControls />}
      {!isSensorSelectionMode && isPointSelectionMode && (
        <>
          <PointSelectionMapClickHandler />
          <PointSelectionMapControls />
          <CustomPointMarker />
        </>
      )}
      {isSensorSelectionMode && (
        <>
          <SensorSelectionMapClickHandler />
          <SensorSelectionControls />
          {/* Spec 043 — sensor stations stay visible on both tabs. On Chuva
              they are interactive (click toggles selection); on Raio they are
              non-interactive reference pins so the user can see where the
              region's stations sit while picking a lightning monitoring point.
              Lightning circle + placed-point marker render only on Raio. */}
          <SensorSelectionMarkersOverlay
            sensorMarkerData={sensorMarkerData}
            selectedSensorIdSet={selectedSensorIdSet}
          />
          <LightningSelectionOverlay />
        </>
      )}
    </MapContainer>
  );
};

/**
 * Spec 043 — observer sub-component for the per-tab markers in
 * sensor-selection mode.
 *  - Chuva: precipitation sensors with click-to-toggle behaviour.
 *  - Raio: ALL forecast stations of the region as non-interactive reference
 *    pins (mirrors Aurora's pattern of keeping forecast pins visible across
 *    tabs as a spatial reference while picking a lightning point).
 * Encapsulating the tab-aware gating in its own observer keeps the parent
 * `ForecastChartPanelMap` a plain function — the legacy container invokes
 * it as `ForecastChartPanelMap({...})`, not as JSX.
 */
const SensorSelectionMarkersOverlay: React.FC<{
  sensorMarkerData: ISensorMeasuredData[];
  selectedSensorIdSet: Set<number | undefined>;
}> = observer(({ sensorMarkerData, selectedSensorIdSet }) => {
  const {
    dataStores: { forecastChartPageStore },
  } = useStores();
  if (forecastChartPageStore.sensorSelectionTab === "lightning") {
    return (
      <>
        {forecastChartPageStore.stations.map((station) => (
          <LightningTabStationReferenceMarker
            key={`station-ref-${station.station_id}-${station.region_id}`}
            stationLat={station.station_lat}
            stationLon={station.station_lon}
          />
        ))}
      </>
    );
  }
  return (
    <>
      {sensorMarkerData.map((sensor) => (
        <SensorSelectionMarker
          key={`sensor-sel-${sensor.station_id}-${sensor.region_id}`}
          sensor={sensor}
          isSelected={selectedSensorIdSet.has(sensor.station_id)}
        />
      ))}
    </>
  );
});

/**
 * Non-interactive station reference pin used on the Raio tab. Uses the same
 * FontAwesome `fa-map-marker-alt` glyph the legacy renders for stations
 * without measured data (see `customMarkerIcon` in CustomMarker.tsx), kept
 * dimmed (50% opacity) so it reads as a reference rather than an actionable
 * pin. `interactive: false` lets clicks pass through to
 * `SensorSelectionMapClickHandler` so a click on top of a station still
 * places the lightning monitoring point.
 */
const LIGHTNING_STATION_REFERENCE_ICON = divIcon({
  className: "lightning-station-reference",
  html: `<i style="color:rgba(46,50,51,0.5)" class="fas fa-map-marker-alt fa-1x"></i>`,
});

const LightningTabStationReferenceMarker: React.FC<{
  stationLat: number;
  stationLon: number;
}> = ({ stationLat, stationLon }) => {
  return (
    <Marker
      position={[stationLat, stationLon]}
      icon={LIGHTNING_STATION_REFERENCE_ICON}
      interactive={false}
    />
  );
};

/**
 * Spec 043 — observer sub-component for the lightning coverage overlay.
 * Renders the coverage circle + the placed-point marker only on the "Raio"
 * tab.
 */
const LightningSelectionOverlay: React.FC = observer(() => {
  const {
    dataStores: { forecastChartPageStore, insightAlertStore },
  } = useStores();

  // Hooks (useMemo) must be called unconditionally — keep them at the top of
  // the component, BEFORE any early `return null`, to satisfy the Rules of
  // Hooks. We use a divIcon (HTML-based) instead of the default Leaflet PNG
  // marker because the default icon's image assets are broken under webpack
  // bundling. The legacy codebase already uses divIcon for every other map
  // pin (see SensorSelectionMarker / iconSelected).
  const lightningIcon = useMemo(
    () =>
      divIcon({
        className: "lightning-point-marker",
        html: `<div style="
          display:flex;align-items:center;justify-content:center;
          width:28px;height:28px;border-radius:50%;
          background:#0884CC;color:white;
          box-shadow:0 2px 6px rgba(0,0,0,0.35);
          border:2px solid white;
        "><svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2'/></svg></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      }),
    [],
  );

  if (forecastChartPageStore.sensorSelectionTab !== "lightning") return null;
  const lightningSlice = insightAlertStore.lightningSelection;
  if (!lightningSlice) return null;
  return (
    <>
      <Circle
        center={[lightningSlice.point.lat, lightningSlice.point.lng]}
        radius={lightningSlice.radiusKm * 1000}
        pathOptions={{
          color: LIGHTNING_COVERAGE_STROKE_COLOR,
          weight: LIGHTNING_COVERAGE_STROKE_WEIGHT,
          fillColor: LIGHTNING_COVERAGE_FILL_COLOR,
          fillOpacity: LIGHTNING_COVERAGE_FILL_OPACITY,
        }}
      />
      <Marker
        position={[lightningSlice.point.lat, lightningSlice.point.lng]}
        icon={lightningIcon}
      />
    </>
  );
});

export default ForecastChartPanelMap;
